import css from './index.module.scss';
import { FormProvider, useFieldArray, useForm, useWatch } from 'react-hook-form';
import {
  DOMESTIC_NON_CONSOLIDATION_ENQUIRY_QUOTE_DEFAULT_VALUE,
  GENERIC_INFORMATION_DOMESTIC_DEFAULT_VALUE,
  getCountryOfOriginOption,
  getCountryOfOriginPayload,
  getMaterialLocationCountryOption,
  getMaterialLocationCountryPayload,
  getProductDetailsPayload,
  toQuoteProductOption
} from '../helper';
import { Button } from '@components/base';
import NonConsolidationDomestic from './components/non-consolidation-domestic';
import CostCalculator from '../cost-calculator';
import { useEffect, useState } from 'react';
import { CURRENCY, MARKET_TYPE, TAX_OPTIONS, WEIGHT_SYSTEM } from '@helpers/constants';
import {
  IFilterState,
  IQuoteFormState,
  IWeightChildOption,
  IWeightSystemOption
} from '@helpers/types/quotes';
import EnquiryGenericInformation from '../generic-information';
import { useNavigate } from 'react-router-dom';
import { TextField } from '@components/common';
import { getUsaGeoLocationBasedOnZipCode } from '@services/util.service';
import { yupResolver } from '@hookform/resolvers/yup';
import { domesticNonConsolidateValidation } from '@helpers/yup/add-quote.schema';

interface IDomesticQuote {
  quoteFormState: IQuoteFormState;
  setQuoteFormState: React.Dispatch<React.SetStateAction<IQuoteFormState>>;
  filterProductOptions: (event: React.SyntheticEvent | null, param?: string) => Promise<void>;
  filterState: IFilterState;
  data?: any;
  onSubmitClose: () => void;
  saveAsDraft: (reqBody: Record<string, unknown>) => Promise<void>;
  editEnquirySubmit: (enquiryId: string, reqBody: Record<string, unknown>) => Promise<void>;
  fetchFormCurrentValue: (data: Record<string, unknown>) => void;
}

const DomesticQuote = (props: IDomesticQuote) => {
  const navigate = useNavigate();

  const [dischargeZip, setDischargeZip] = useState({
    placeOfDischarge: '',
    zipCode: ''
  });

  const { zipCode } = dischargeZip;

  const {
    quoteFormState,
    setQuoteFormState,
    filterProductOptions,
    filterState,
    data,
    onSubmitClose,
    saveAsDraft,
    editEnquirySubmit,
    fetchFormCurrentValue
  } = props;

  const { fcl, consolidation, mtt } = filterState;

  const {
    selectedWeightSystemChildOptions,
    productOptions,
    selectedCurrency,
    enquiryType,
    firstFormData,
    duplicateEnquiry,
    isDraft,
    enquiryId
  } = quoteFormState;

  const form = useForm<any>({
    mode: 'onSubmit',
    reValidateMode: 'onChange',
    resolver: yupResolver(domesticNonConsolidateValidation),
    defaultValues: {
      genericInformation: GENERIC_INFORMATION_DOMESTIC_DEFAULT_VALUE,
      quote: [DOMESTIC_NON_CONSOLIDATION_ENQUIRY_QUOTE_DEFAULT_VALUE]
    }
  });

  const { control, handleSubmit, getValues, reset, setValue } = form;

  const {
    fields: fields,
    append: append,
    remove: remove
  } = useFieldArray({
    name: 'quote',
    control
  });

  useEffect(() => {
    fields.forEach((_, index) => {
      if (dischargeZip.zipCode !== null && dischargeZip.zipCode !== '') {
        setValue(`quote.${index}.incoTermsZipCode`, dischargeZip.zipCode);
      }
      if (dischargeZip.placeOfDischarge !== null && dischargeZip.placeOfDischarge !== '') {
        setValue(`quote.${index}.incoTermsLocation`, dischargeZip.placeOfDischarge);
      }
    });
  }, [dischargeZip, fields.length]);

  const constructReqBody = (data: any, isDraftAction = false) => {
    const { genericInformation, quote } = data;
    const reqBody: any = {
      currency_type_one: genericInformation.currency1?.value,
      weight_system: genericInformation.weightSystem?.value,
      deal_name: genericInformation.draftDealName,
      primary_unit: genericInformation.primaryUnit?.value,
      tax_type: genericInformation?.taxType?.value,
      market_type: MARKET_TYPE.DOMESTIC,
      is_draft: isDraftAction,
      quotes: quote.map((item: any) => {
        return {
          product_details: getProductDetailsPayload(item?.productName),
          quote_id: item.quote_id,
          packaging: item.productPackaging,
          origin: getCountryOfOriginPayload(item.countryOfOrigin),
          material_location_country: getMaterialLocationCountryPayload(
            item.materialLocationCountry
          ),
          quantity: item.dealQuantity,
          quantity_unit: item.dealQuantityUnit.value,
          costs: {
            base_price: {
              currency: item.base_price?.currency?.value,
              value: item.base_price?.value,
              cost_id: item.base_price?.cost_id,
              unit_of_weight: item.base_price?.weightUnit?.value
            },
            final_price: {
              currency: data.genericInformation.currency1.value,
              value: Math.round(item.finalQuote_selling_price?.value * 100) / 100,
              cost_id: item.finalQuote_selling_price?.cost_id,
              unit_of_weight: item.finalQuote_selling_price?.weightUnit?.value
            },
            additional_cost: {
              currency: item.additionalCost?.currency?.value,
              value: item.additionalCost?.value,
              cost_id: item.additionalCost?.cost_id
            },
            total_local_freight: {
              currency: item.totalLocalFreight?.currency?.value,
              value: item.totalLocalFreight?.value,
              cost_id: item.totalLocalFreight?.cost_id
            },
            warehousing_cost: {
              currency: item.warehousingCost?.currency?.value,
              value: item.warehousingCost?.value,
              cost_id: item.warehousingCost?.cost_id
            }
          },
          moq: item.moq,
          moq_unit: item.moqUnit.value,
          weight_one_container: item.totalWeight,
          weight_one_container_unit: item.totalWeightUnit?.value,
          insurance_cost: item.insuranceCost,
          finance_cost: item.financeCost,
          profit_margin: item.profitMarginExpected,
          discount: item.discount,
          zip_code: item.incoTermsZipCode,
          deal_incoterm_location: item.incoTermsLocation
        };
      })
    };

    reqBody.sourcing_type = mtt ? 'MTT' : 'NON_MTT';
    reqBody.container_type = fcl ? 'FTL' : 'LTL';
    reqBody.is_consolidation = consolidation;

    return reqBody;
  };

  const getGeoLocation = async (zipCode: string) => {
    const response = await getUsaGeoLocationBasedOnZipCode(zipCode);
    if (response?.data) {
      const { data } = response;
      const placeName = data?.places[0]?.['place name'];
      const state = data?.places[0].state;
      const country = data?.country;
      const location = `${placeName}, ${state}`;
      setDischargeZip((prevState: any) => ({
        ...prevState,
        placeOfDischarge: location
      }));
    } else {
      // notify({
      //   severity: 'error',
      //   message: 'Please enter a valid US Zip Code'
      // });
    }
  };

  const formWatch = useWatch({
    control
  });
  useEffect(() => {
    if (formWatch) {
      const reqBody = constructReqBody(formWatch, true);
      fetchFormCurrentValue(reqBody);
    }
  }, [formWatch]);

  useEffect(() => {
    if (zipCode && zipCode.length === 5) {
      getGeoLocation(zipCode);
    }
  }, [zipCode]);

  const onFormSubmit = (data: any, isDraftAction = false) => {
    const currentFormData = getValues();
    const reqBody: any = constructReqBody(data, isDraftAction);
    if (isDraft && !isDraftAction) {
      reqBody.convert_from_draft_to_enquiry = true;
    }

    if (!isDraft && isDraftAction) {
      saveAsDraft(reqBody);
      return;
    }
    if (isDraft && isDraftAction) {
      editEnquirySubmit(enquiryId ?? '', reqBody);
      onSubmitClose();
      navigate('/quote?activeTab=cost_calculator&page=1');
      return;
    }

    setQuoteFormState((prevState: any) => ({
      ...prevState,
      firstFormReqBody: reqBody,
      activeStep: prevState.activeStep + 1,
      firstFormData: currentFormData
    }));
  };

  const handleAddQuote = () => {
    append(DOMESTIC_NON_CONSOLIDATION_ENQUIRY_QUOTE_DEFAULT_VALUE);
  };

  const handleDuplicateQuote = (index: number) => {
    const currentQuote = getValues(`quote.${index}`);
    append({
      ...currentQuote,
      quote_id: null,
      isExpanded: true,
      incoTermsZipCode: dischargeZip.zipCode,
      incoTermsLocation: dischargeZip.placeOfDischarge
    });
    const updatedQuotes = form.getValues('quote').map((item: any, i: number, arr: any) => {
      if (i === arr.length - 1) {
        return { ...item, isExpanded: true };
      } else {
        return { ...item, isExpanded: false };
      }
    });
    form.setValue('quote', updatedQuotes);
  };

  const currency1Watch = useWatch({
    control,
    name: `genericInformation.currency1`
  });

  const primaryUnitWatch = useWatch({
    control,
    name: 'genericInformation.primaryUnit'
  });

  const quoteWatch = useWatch({
    control,
    name: 'quote'
  });

  useEffect(() => {
    if (data) {
      const QuoteZipcode = data?.quotes[0]?.zip_code;
      const QuoteLocation = data?.quotes[0]?.deal_incoterm_location;
      setDischargeZip({
        zipCode: QuoteZipcode || '',
        placeOfDischarge: QuoteLocation || ''
      });
    }
  }, [data]);

  useEffect(() => {
    if (firstFormData) {
      reset(firstFormData);

      const selectedChildWeights = WEIGHT_SYSTEM.find(
        (item: IWeightSystemOption) =>
          item.value === firstFormData.genericInformation.weightSystem.value
      )?.childOptions;

      setQuoteFormState((prevState: any) => ({
        ...prevState,
        selectedWeightSystemChildOptions: selectedChildWeights || [],
        selectedCurrency: [
          CURRENCY.find((item) => item.value === firstFormData.genericInformation.currency1.value)
        ]
      }));

      if (firstFormData.quote && firstFormData.quote.length > 0) {
        setDischargeZip({
          zipCode: firstFormData.quote[0].incoTermsZipCode || '',
          placeOfDischarge: firstFormData.quote[0].incoTermsLocation || ''
        });
      }
    } else if (data) {
      const selectedChildWeights = WEIGHT_SYSTEM.find(
        (item: IWeightSystemOption) => item.value === data.weight_system
      )?.childOptions;

      const primaryCurrency = CURRENCY.find((item) => item.value === data.currency_type_one);
      const primaryUnit = selectedChildWeights?.find(
        (item: IWeightChildOption) => item.value === data?.primary_unit
      );

      form.reset({
        genericInformation: {
          currency1: CURRENCY.find((item) => item.value === data.currency_type_one),
          weightSystem: WEIGHT_SYSTEM?.find(
            (item: IWeightSystemOption) => item.value === data.weight_system
          ),
          draftDealName: data?.deal_name,
          primaryUnit: selectedChildWeights?.find(
            (item: IWeightChildOption) => item.value === data?.primary_unit
          ),
          taxType: TAX_OPTIONS.find((item) => item.value === data?.tax_type) || null
        },
        quote: (data?.quotes || []).map((quote: any) => {
          return {
            quote_id: quote.quote_id,
            isExpanded: true,
            productName: toQuoteProductOption(quote.product_details),
            productPackaging: quote?.packaging,
            dealQuantity: quote?.quantity,
            dealQuantityUnit: quote?.quantity_unit
              ? selectedChildWeights?.find(
                  (i: IWeightChildOption) => i.value === quote.quantity_unit
                )
              : primaryUnit,
            base_price: {
              value: quote.base_price ? quote.base_price?.value : 0,
              currency: quote?.base_price
                ? CURRENCY.find((i) => i.value === quote?.base_price?.currency)
                : primaryCurrency,
              cost_id: duplicateEnquiry ? null : quote?.base_price?.cost_id,
              manualOverride: true,
              weightUnit: quote?.base_price?.unit_of_weight
                ? selectedChildWeights?.find(
                    (i: IWeightChildOption) => i.value === quote?.base_price?.unit_of_weight
                  )
                : primaryUnit
            },
            moq: quote?.moq,
            moqUnit: quote?.moq_unit
              ? selectedChildWeights?.find((i: IWeightChildOption) => i.value === quote?.moq_unit)
              : primaryUnit,
            totalWeight: quote?.weight_one_container,
            totalWeightUnit: quote?.weight_one_container_unit
              ? selectedChildWeights?.find(
                  (item: IWeightChildOption) => item.value === quote?.weight_one_container_unit
                )
              : primaryUnit,
            totalLocalFreight: {
              value: quote.total_local_freight ? quote.total_local_freight?.value : 0,
              currency: quote.total_local_freight
                ? CURRENCY.find((item) => item.value === quote?.total_local_freight?.currency)
                : primaryCurrency,
              cost_id: duplicateEnquiry ? null : quote.total_local_freight?.cost_id
            },
            incoTermsLocation: quote?.deal_incoterm_location || data.place_of_discharge,
            incoTermsZipCode: quote?.zip_code || data.zip_code,
            additionalCost: {
              value: quote.additional_cost ? quote.additional_cost?.value : 0,
              currency: quote?.additional_cost
                ? CURRENCY.find((item) => item?.value === quote?.additional_cost?.currency)
                : primaryCurrency,
              cost_id: duplicateEnquiry ? null : quote?.additional_cost?.cost_id
            },
            insuranceCost: quote?.insurance_cost,
            financeCost: quote?.finance_cost,
            profitMarginExpected: quote?.profit_margin,
            discount: quote?.discount,
            finalQuote_selling_price: {
              value: quote.final_price ? quote.final_price?.value : 0,
              currency: quote?.final_price
                ? CURRENCY.find((item) => item.value === quote?.final_price?.currency)
                : primaryCurrency,
              cost_id: duplicateEnquiry ? null : quote?.final_price?.cost_id,
              weightUnit: quote?.final_price?.unit_of_weight
                ? selectedChildWeights?.find(
                    (item: IWeightChildOption) => item.value === quote?.final_price?.unit_of_weight
                  )
                : primaryUnit,
              manualOverride: true
            },
            countryOfOrigin: getCountryOfOriginOption(quote?.origin, quote?.origin_display_value),
            materialLocationCountry: getMaterialLocationCountryOption(
              quote?.material_location_country,
              quote?.material_location_country_display_value
            ),
            warehousingCost: {
              value: quote.warehousing_cost ? quote.warehousing_cost?.value : 0,
              currency: quote?.warehousing_cost
                ? CURRENCY.find((item) => item.value === quote?.warehousing_cost?.currency)
                : primaryCurrency,
              cost_id: duplicateEnquiry ? null : quote?.warehousing_cost?.cost_id
            }
          };
        })
      });
    }
  }, [data]);

  return (
    <>
      <FormProvider {...form}>
        <form
          noValidate
          onSubmit={handleSubmit((data) => onFormSubmit(data))}
          className={css.mainWrapper}
        >
          <CostCalculator
            filters={filterState}
            weightOptions={selectedWeightSystemChildOptions}
            currency={currency1Watch}
            primaryUnitWatch={primaryUnitWatch}
            quote={quoteWatch}
            enquiryType={enquiryType}
          />

          <EnquiryGenericInformation
            setQuoteFormState={setQuoteFormState}
            selectedWeightSystemChildOptions={selectedWeightSystemChildOptions}
            enquiryType={enquiryType}
          />

          <div className={css.rowWrapper}>
            <TextField
              label="Zip Code"
              type="number"
              value={dischargeZip.zipCode ?? ''}
              onChange={(e: any) => {
                setDischargeZip((prevState) => ({
                  ...prevState,
                  zipCode: e.target.value
                }));
              }}
            />
            <TextField
              label="Place of Discharge"
              type="text"
              enterKeyHint="next"
              value={dischargeZip.placeOfDischarge ?? ''}
              onChange={(e: any) => {
                setDischargeZip((prevState) => ({
                  ...prevState,
                  placeOfDischarge: e.target.value
                }));
              }}
            />
          </div>

          {fields.map((item: any, index: number) => {
            const handleDeleteClick = () => {
              remove(index);
            };
            return (
              <NonConsolidationDomestic
                key={item.id}
                index={index}
                isEnquiry={true}
                fieldNames={{
                  isExpanded: `quote.${index}.isExpanded`,
                  productName: `quote.${index}.productName`,
                  productPackaging: `quote.${index}.productPackaging`,
                  dealQuantity: `quote.${index}.dealQuantity`,
                  dealQuantityUnit: `quote.${index}.dealQuantityUnit`,
                  moq: `quote.${index}.moq`,
                  moqUnit: `quote.${index}.moqUnit`,
                  incoTermsZipCode: `quote.${index}.incoTermsZipCode`,
                  incoTermsLocation: `quote.${index}.incoTermsLocation`,
                  countryOfOrigin: `quote.${index}.countryOfOrigin`,
                  materialLocationCountry: `quote.${index}.materialLocationCountry`,
                  totalWeight: `quote.${index}.totalWeight`,
                  totalWeightUnit: `quote.${index}.totalWeightUnit`,
                  totalLocalFreightPrice: `quote.${index}.totalLocalFreight.value`,
                  totalLocalFreightCurrency: `quote.${index}.totalLocalFreight.currency`,
                  basePrice: `quote.${index}.base_price.value`,
                  basePriceCurrency: `quote.${index}.base_price.currency`,
                  basePriceWeightUnit: `quote.${index}.base_price.weightUnit`,
                  basePriceManualOverride: `quote.${index}.base_price.manualOverride`,
                  warehousingCostPrice: `quote.${index}.warehousingCost.value`,
                  warehousingCostCurrency: `quote.${index}.warehousingCost.currency`,
                  additionalCostPrice: `quote.${index}.additionalCost.value`,
                  additionalCostCurrency: `quote.${index}.additionalCost.currency`,
                  financeCost: `quote.${index}.financeCost`,
                  profitMarginExpected: `quote.${index}.profitMarginExpected`,
                  discount: `quote.${index}.discount`,
                  insuranceCost: `quote.${index}.insuranceCost`,
                  finalQuoteSellingPrice: `quote.${index}.finalQuote_selling_price.value`,
                  finalQuoteSellingPriceCurrency: `quote.${index}.finalQuote_selling_price.currency`,
                  finalQuoteSellingPriceWeightUnit: `quote.${index}.finalQuote_selling_price.weightUnit`,
                  finalQuoteSellingPriceManualOverride: `quote.${index}.finalQuote_selling_price.manualOverride`
                }}
                handleDuplicateQuote={handleDuplicateQuote}
                handleDeleteClick={handleDeleteClick}
                fieldLength={fields.length}
                filterProductOptions={filterProductOptions}
                productOptions={productOptions}
                selectedWeightSystemChildOptions={selectedWeightSystemChildOptions}
                selectedCurrency={selectedCurrency}
                primaryUnitWatch={primaryUnitWatch}
                currency1Watch={currency1Watch}
              />
            );
          })}

          <Button
            variant="outlined-secondary"
            onClick={() => {
              handleAddQuote();
            }}
          >
            Add Product
          </Button>
          <div className={css.actionButtons}>
            <Button
              variant="text"
              onClick={() => {
                onFormSubmit(getValues(), true);
              }}
            >
              Save as Draft
            </Button>
            <Button variant="contained" type="submit">
              Save and Proceed
            </Button>
          </div>
        </form>
      </FormProvider>
    </>
  );
};

export default DomesticQuote;
